import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { useUserOrgs } from '@/features/org/hooks/use-user-orgs';
import { OrgId } from '@/features/org/types/org-id';

interface CurrentOrgIdState {
  orgId: OrgId | null;
  setOrgId: (orgId: OrgId) => void;
  resetOrgId: () => void;
}

export const useCurrentOrgIdStore = create<CurrentOrgIdState>()(
  persist(
    (set) => ({
      orgId: null,
      setOrgId: (orgId) => set({ orgId }),
      resetOrgId: () => set({ orgId: null }),
    }),
    {
      name: 'current-org-id',
    },
  ),
);

export const useCurrentOrgId = () => {
  return useCurrentOrgIdStore((state) => state.orgId);
};

export const useSetCurrentOrgId = () => {
  return useCurrentOrgIdStore((state) => state.setOrgId);
};

export const useResetOrgId = () => {
  return useCurrentOrgIdStore((state) => state.resetOrgId);
};

export const useSyncCurrentOrgWithUserOrgs = () => {
  const { data: userOrgs, isFetching: isUserOrgsFetching } = useUserOrgs();
  const currentOrgId = useCurrentOrgId();
  const resetOrgId = useResetOrgId();
  const setCurrentOrgId = useSetCurrentOrgId();

  useEffect(() => {
    if (isUserOrgsFetching) return;
    if (!userOrgs?.length) {
      resetOrgId();
      return;
    }

    if (currentOrgId && !userOrgs.some((org) => org.id === currentOrgId)) {
      resetOrgId();
    }

    if (!currentOrgId && userOrgs.length > 0) {
      // first org with a non null name
      const firstOrg = userOrgs.find((org) => org.name !== null);
      if (firstOrg) {
        setCurrentOrgId(firstOrg.id);
      } else {
        setCurrentOrgId(userOrgs[0].id);
      }
    }
  }, [userOrgs, currentOrgId, resetOrgId, isUserOrgsFetching]);
};
